import { useEffect, useState } from "react";
import axios from "axios";
import { SUPPORTED_CHAINS } from "@/lib/wallet";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Icons for non-EVM & extra chains not covered by the hardcoded wagmi list.
const EXTRA_ICONS = {
  bitcoin: "https://icons.llamao.fi/icons/chains/rsz_bitcoin.jpg",
  tron: "https://icons.llamao.fi/icons/chains/rsz_tron.jpg",
  sui: "https://icons.llamao.fi/icons/chains/rsz_sui.jpg",
};

const TYPE_LABEL = { EVM: "EVM", SVM: "SVM", MVM: "SUI", TVM: "TRON", UTXO: "BTC" };

const HARD_BY_ID = new Map(SUPPORTED_CHAINS.map((c) => [c.id, c]));

let _cache = null;
let _promise = null;

function normalise(raw) {
  const key = (raw.key || raw.name || "").toLowerCase().replace(/[^a-z0-9]+/g, "-");
  const hard = HARD_BY_ID.get(raw.id);
  return {
    id: raw.id,
    key: hard?.key || key,
    name: hard?.name || raw.name,
    symbol: hard?.symbol || raw.nativeToken?.symbol || raw.coin || "",
    icon: hard?.icon || raw.logoURI || EXTRA_ICONS[key] || "",
    chainType: raw.chainType || "EVM",
    isSolana: raw.chainType === "SVM",
    isSui: raw.chainType === "MVM",
    isTron: raw.chainType === "TVM",
    isBitcoin: raw.chainType === "UTXO",
    isTestnet: /testnet|sepolia|goerli/i.test(raw.name || ""),
  };
}

export function isSignableSource(chain) {
  // We support wallet signing on EVM, Solana, Sui, Tron, and Bitcoin.
  return ["EVM", "SVM", "MVM", "TVM", "UTXO"].includes(chain?.chainType);
}

export async function fetchChains() {
  if (_cache) return _cache;
  if (_promise) return _promise;
  _promise = (async () => {
    try {
      const { data } = await axios.get(`${API}/lifi/chains`);
      const raw = Array.isArray(data?.chains) ? data.chains : [];
      const list = raw
        .map(normalise)
        .filter((c) => !c.isTestnet)
        // Stable order: EVM first (Ethereum, Base, Arbitrum then rest), then SVM, then Sui, Tron, BTC
        .sort((a, b) => {
          const rank = (c) => {
            if (c.chainType === "EVM") {
              const pri = { 1: 0, 8453: 1, 42161: 2, 10: 3, 137: 4, 56: 5, 43114: 6 };
              return 0 * 1000 + (pri[c.id] ?? 100 + (c.id % 100));
            }
            if (c.chainType === "SVM") return 2000;
            if (c.chainType === "MVM") return 3000;
            if (c.chainType === "TVM") return 4000;
            if (c.chainType === "UTXO") return 5000;
            return 9000;
          };
          return rank(a) - rank(b);
        });
      _cache = list;
      return list;
    } catch (e) {
      _cache = SUPPORTED_CHAINS.map((c) => ({
        ...c,
        chainType: c.isSolana ? "SVM" : "EVM",
        isTestnet: false,
      }));
      return _cache;
    }
  })();
  return _promise;
}

export function useChains() {
  const [chains, setChains] = useState(_cache || null);
  useEffect(() => {
    let cancel = false;
    fetchChains().then((list) => { if (!cancel) setChains(list); });
    return () => { cancel = true; };
  }, []);
  return chains;
}

export function chainTypeLabel(chain) {
  return TYPE_LABEL[chain?.chainType] || "EVM";
}
